import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRole } from "@/contexts/RoleContext";
import { useCurrentUserId } from "@/hooks/use-current-user";
import { getPageTourSteps, getPageKeyFromPath } from "@/components/spotlight-tour/page-tour-steps";
import { TourStep } from "@/components/spotlight-tour/tour-steps";
import { useLocation } from "react-router-dom";

interface UsePageTourResult {
  isActive: boolean;
  steps: TourStep[];
  pageKey: string | null;
  completeTour: () => Promise<void>;
}

/**
 * Hook to manage per-page first-visit spotlight tours.
 * Returns tour state for the current page — only activates if:
 * 1. The page has defined tour steps for the user's role
 * 2. The user hasn't completed the tour for this page yet
 * 3. The main onboarding tour is not running
 */
export function usePageTour(onboardingActive: boolean): UsePageTourResult {
  const { userRole } = useRole();
  const { data: userId } = useCurrentUserId();
  const location = useLocation();
  const [isActive, setIsActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [pageKey, setPageKey] = useState<string | null>(null);
  const [checkedPages, setCheckedPages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (onboardingActive) {
      setIsActive(false);
      return;
    }
    if (!userId) return;

    const key = getPageKeyFromPath(location.pathname);
    if (!key) return;

    // Don't re-check pages we've already checked this session
    if (checkedPages.has(key)) return;

    const tourSteps = getPageTourSteps(key, userRole);
    if (!tourSteps) return;

    const checkPageTour = async () => {
      const { data } = await supabase
        .from("user_page_tours")
        .select("id")
        .eq("user_id", userId)
        .eq("page_key", key)
        .maybeSingle();

      setCheckedPages((prev) => new Set(prev).add(key));

      if (!data) {
        // First visit — activate tour after a brief delay
        setPageKey(key);
        setSteps(tourSteps);
        setTimeout(() => setIsActive(true), 800);
      }
    };

    checkPageTour();
  }, [location.pathname, userRole, onboardingActive, checkedPages, userId]);

  const completeTour = useCallback(async () => {
    setIsActive(false);
    if (!pageKey || !userId) return;

    await supabase.from("user_page_tours").insert({
      user_id: userId,
      page_key: pageKey,
    });
  }, [pageKey, userId]);

  return { isActive, steps, pageKey, completeTour };
}
